import mapboxgl, { type LngLatLike, NavigationControl, ScaleControl } from 'mapbox-gl';
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import 'mapbox-gl/dist/mapbox-gl.css';
import { useSidebar } from '@/components/atoms/sidebar';
import { FEATURE_FLAGS } from '@/features/api/constants';
import { useFeatureFlag } from '@/features/api/hooks/useFeatureFlag';
import { HttpService } from '@/features/api/http.service';
import {
  DIALOG_ANCHOR_X_COLLAPSED,
  DIALOG_ANCHOR_X_EXPANDED,
  mapChromeShiftsForSidebar,
} from '@/features/map/components/mapBarGeometry';
import { cn } from '@/libs/utils/cn';
import type { IDealList, ILocation } from '@/types/api';
import {
  COMPETITORS_TILES_PATH,
  LayerId,
  MAP_VIEWPORT_MIN_HEIGHT_CLASS,
  TRAFFIC_TILES_PATH,
  ZONES_TILES_PATH,
} from '../constants';
import { useDealsInteractions } from '../hooks/useDealsInteractions';
import type { LayerVisibilityState } from '../hooks/useLayerVisibilityFilters';
import { useMap } from '../hooks/useMap';
import { usePointOfInterestInteractions } from '../hooks/usePointOfInterestInteractions';
import { useSelectedPlacePin } from '../hooks/useSelectedPlacePin';
import { useStationsInteractions } from '../hooks/useStationsInteractions';
import { useCompetitorHeatmapHover } from '../hooks/useCompetitorHeatmapHover';
import { useTrafficTilesHover } from '../hooks/useTrafficTilesHover';
import { type SelectedZone, useZoneInteractions } from '../hooks/useZoneInteractions';
import { useZonePopup } from '../hooks/useZonePopup';
import { useDealsQuery } from '../services/deals.api';
import {
  applyCompetitorsTilesVisibility,
  applyTrafficTilesVisibility,
  type StationVisibilityState,
} from '../utils/applyLayerVisibility';
import { holdPlacesTheTileDrops, keepPointsOfInterest } from '../utils/basemap';
import { initializeMapIcons } from '../utils/initializeMapIcons';
import { ZONE_TYPES, type ZoneType } from '../utils/zoneColors';
import { Map3dToggleControl } from '../utils/Map3dToggleControl';
import {
  COMPETITOR_TIER_LAYER_IDS,
  MapLayersService,
  type OwnerSelection,
} from '../utils/MapLayersService';
import { elementExtendsMapChrome } from '../utils/mapChromeExtent';
import type { PointOfInterestPlace } from '../utils/pointOfInterest';
import { ALL_PLACES_VISIBLE, type PlaceVisibility } from '../constants/placeSiteTypes';
import { SatelliteToggleControl } from '../utils/SatelliteToggleControl';
import { PointOfInterestPopup } from './PointOfInterestPopup';
import { StationMapPopup } from './StationMapPopup';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

/** Module-level so the default prop value is referentially stable across renders. */
const EVERY_OWNER: OwnerSelection = [];

/** Street-level zoom for picking a point beside a place. A closer view stays. */
const PLACE_DROP_ZOOM = 16;

/** Widest the scale bar may draw; Mapbox rounds the distance down to a whole unit. */
const SCALE_CONTROL_MAX_WIDTH_PX = 100;

/**
 * State the generic shell exposes to its primary-content consumer (render-prop).
 * The consumer (e.g. the project map) uses this to wire its own
 * project-specific layers, clusters and interactions on top of the shell's
 * Mapbox instance without re-implementing the lifecycle.
 */
export interface MapShellRenderState {
  /** Stable ref to the live Mapbox map (null until first mount). */
  map: React.RefObject<mapboxgl.Map | null>;
  /** True once the Mapbox `load` event has fired. */
  isMapLoaded: boolean;
  /** True once base controls + icon assets are installed on the map. */
  imagesInitialized: boolean;
  /** Container element backing the map, for anchoring portalled overlays. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /**
   * The mirrored deals the shell polls for the map source, undefined until the mirror answers.
   * Exposed so a consumer reading the same set — the zone pipeline counts every deal, placed or
   * not — reuses this payload instead of opening a second observer on it.
   */
  deals?: IDealList;
  /** Whether the place card, which the shell owns, holds the left slot. */
  placeOpen?: boolean;
}

export interface MapShellProps {
  /**
   * Identity key for the map instance. When it changes the underlying Mapbox
   * map is torn down and recreated (see `useMap`). For the project map this is
   * the project id; other consumers pass a stable id.
   */
  mapId: string;
  /**
   * The selected zone's name. Hides the wide-zoom zone cluster that would otherwise sit over it
   * saying "1". Absent leaves that bubble alone. Deals stay on the map whatever zone is selected.
   */
  selectedZoneName?: string;
  /** The selected deal from `?deal=<id>` — owned by the surface, like the zone and location selections. */
  selectedDealId?: string | null;
  onDealSelect?: (dealId: string | null) => void;
  /**
   * Starts placing a workspace location from a clicked place. The shell frames
   * that place, then the consumer chooses the spot. Absent when there is no
   * workspace to add it to, which disables the card's button.
   */
  onCreateLocation?: (place: PointOfInterestPlace) => void;
  /** A deal, location, or zone already owns the corner, so the place card closes. */
  cornerCardOpen?: boolean;
  /** A place card opened; the surface drops the deal, location, or zone in that corner. */
  onPlaceOpen?: () => void;
  /** Fallback centre used when no fit-bounds source is available. */
  defaultCenter: LngLatLike;
  /** Zoom used with `defaultCenter`, i.e. when nothing is fitted to. */
  defaultZoom: number;
  /**
   * Points the map fits to on first idle. The project map passes its
   * locations; other consumers may pass an empty array (centres on
   * `defaultCenter`).
   */
  fitToLocations: ILocation[];
  /** Drives the initial zoom for a single-location fit. */
  currentLocation?: ILocation;
  /**
   * Whether the zones overlay should be active (in addition to the feature
   * flag and map-loaded gates). The project map drives this from its sidebar
   * toggle; pass `true` to follow the flag/load gates only.
   */
  zonesOverlayVisible: boolean;
  /** The owners whose zones and deals stay visible; empty restricting nothing. */
  ownerSelection?: OwnerSelection;
  /** Explicit selected types; empty hides every zone type. */
  visibleZoneTypes?: readonly ZoneType[];
  /**
   * AADT ranges the left panel keeps visible. Drives the map-level traffic
   * tiles, whose layer this shell owns; the selected location's max-AADT
   * highlight is filtered by its own consumer through `applyMaxAadtVisibility`.
   */
  visibleTrafficRanges?: LayerVisibilityState['traffic'];
  /** Station toggles for the map-level competitor tiles, whose layer this shell owns. */
  visibleStationFilters?: StationVisibilityState;
  /** Wide-zoom session clusters. Off until the legend eye opens them. */
  clustersVisible?: boolean;
  /** Site-type chips for the basemap points of interest. Every type when omitted. */
  visiblePlaces?: PlaceVisibility;
  /**
   * What a zone-polygon click should do. The project map renders a read-only
   * popup at the selection; other consumers (e.g. the map) navigate.
   * Returning a node renders it (portalled into the live Mapbox popup);
   * returning `null` performs no popup (the handler did the work itself).
   */
  renderZoneSelection?: (args: {
    selectedZone: SelectedZone;
    clearSelection: () => void;
    popupContainer: HTMLDivElement | null;
  }) => ReactNode;
  /**
   * What a selected deal renders — the `renderZoneSelection` seam for the deals
   * layer. The shell owns the selection mechanics (open ring, clear on close);
   * the consumer owns the card.
   */
  renderDealSelection?: (args: {
    dealId: string;
    clearSelection: () => void;
    map: mapboxgl.Map;
    isMapLoaded: boolean;
    imagesInitialized: boolean;
    dialogContainer?: HTMLElement;
  }) => ReactNode;
  /**
   * Called (via `useEffect`) whenever the user selects a zone polygon. Runs
   * after render — safe for side effects such as navigation. The map surface
   * uses it to select the zone (`?zone=<id>`) without triggering a
   * render-phase side effect. Optional: omitting it is a no-op.
   */
  onZoneSelected?: (zone: SelectedZone) => void;
  /**
   * Called when a click landing on no zone deselects, so a consumer holding its own selection
   * (the map surface's `?zone=`) drops it too. Optional: omitting it is a no-op.
   */
  onZoneDeselected?: () => void;
  /**
   * When `.current` is true at click time, the click must not select anything on
   * the map — neither a zone nor a station — because something else (e.g. the map
   * surface's armed add-pin tool) is claiming it. See `useZoneInteractions`'s
   * param doc for why this has to be a ref rather than a boolean prop. Optional:
   * omitting it never suppresses selection.
   */
  suppressSelectionRef?: React.RefObject<boolean>;
  /**
   * Primary map content (project layers, clusters, metric panels, …). Rendered
   * as a render-prop so the consumer can react to the shell's map state.
   */
  children?: (state: MapShellRenderState) => ReactNode;
  /** Zoom NavigationControl position; defaults to top-right (project-map regression safety). */
  navControlPosition?: 'top-right' | 'bottom-right';
}

/**
 * Generic, consumer-agnostic map shell. Owns the Mapbox instance, base
 * controls (navigation + satellite toggle), icon assets, the zones-overlay
 * wiring (tile layer + interactions + popup). Consumer-specific wiring is
 * supplied through `children`.
 */
export function MapShell(props: MapShellProps) {
  const {
    mapId,
    selectedZoneName,
    selectedDealId = null,
    onDealSelect,
    onCreateLocation,
    cornerCardOpen = false,
    onPlaceOpen,
    defaultCenter,
    defaultZoom,
    fitToLocations,
    currentLocation,
    zonesOverlayVisible: overlayVisible,
    ownerSelection = EVERY_OWNER,
    visibleZoneTypes = ZONE_TYPES,
    visibleTrafficRanges,
    visibleStationFilters,
    clustersVisible = false,
    visiblePlaces = ALL_PLACES_VISIBLE,
    renderZoneSelection,
    renderDealSelection,
    onZoneSelected,
    onZoneDeselected,
    suppressSelectionRef,
    children,
    navControlPosition = 'top-right',
  } = props;

  const sidebarCtxt = useSidebar();
  const shellRef = useRef<HTMLDivElement>(null);
  const [shiftChrome, setShiftChrome] = useState(() =>
    mapChromeShiftsForSidebar(typeof window === 'undefined' ? 0 : window.innerWidth),
  );

  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const sync = () => {
      const width = el.getBoundingClientRect().width;
      if (width === 0) return;
      setShiftChrome((prev) => {
        const next = mapChromeShiftsForSidebar(width);
        return prev === next ? prev : next;
      });
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--dialog-anchor-x',
      sidebarCtxt.state === 'expanded' ? DIALOG_ANCHOR_X_EXPANDED : DIALOG_ANCHOR_X_COLLAPSED,
    );
    return () => {
      root.style.removeProperty('--dialog-anchor-x');
    };
  }, [sidebarCtxt.state]);

  const mapContainer = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapIdRendered = useRef<number | null>(null);
  const [imagesInitialized, setImagesInitialized] = useState(false);

  const { map, isMapLoaded } = useMap(
    mapContainer,
    defaultCenter,
    defaultZoom,
    mapId,
    fitToLocations,
    currentLocation,
  );

  // ── Hunting-zones overlay ─────────────────────────────────────────────────
  // Flag-gated, read-only, served as vector tiles: Mapbox requests the tiles the
  // viewport needs, so there is no bbox to track and no fetch to debounce.
  //
  // Both flags are required: the tile endpoint is one route behind
  // `map_tiles_enabled` whatever the layer, so with it off zones would be
  // requested and answered 404. `zones_overlay_enabled` still owns whether the
  // overlay and its control exist at all.
  const mapTilesEnabled = useFeatureFlag(FEATURE_FLAGS.map_tiles_enabled);
  const zonesOverlayEnabled =
    useFeatureFlag(FEATURE_FLAGS.zones_overlay_enabled) && mapTilesEnabled;
  const zonesEnabled = zonesOverlayEnabled && isMapLoaded && overlayVisible;

  // ── Hunting-zones interactions + popup ───────────────────────────────────
  // The listeners bind once (see the setup effect), so the deselect callback has to stay current
  // through a ref — the same reason useDealsInteractions holds onDealSelect that way.
  const onZoneDeselectedRef = useRef(onZoneDeselected);
  onZoneDeselectedRef.current = onZoneDeselected;

  // Filled in once the place hook exists. The zone listeners bind once and read this at click time.
  const openPlaceRef = useRef<((place: PointOfInterestPlace) => void) | null>(null);

  const { setupZoneInteractions, selectedZone, clearSelection } = useZoneInteractions(
    map,
    suppressSelectionRef,
    () => onZoneDeselectedRef.current?.(),
    openPlaceRef,
  );
  const popupContainer = useZonePopup(map, selectedZone, clearSelection);

  async function initializeAll(currentMap: mapboxgl.Map) {
    try {
      // Add controls — important to add before any other layers.
      currentMap.addControl(
        new NavigationControl({ showCompass: true, visualizePitch: true }),
        navControlPosition,
      );
      currentMap.addControl(new Map3dToggleControl(), navControlPosition);
      currentMap.addControl(
        new ScaleControl({ maxWidth: SCALE_CONTROL_MAX_WIDTH_PX, unit: 'metric' }),
        'bottom-right',
      );
      // Custom controls
      currentMap.addControl(new SatelliteToggleControl(), 'bottom-left');
    } catch (_e) {
      throw new Error('Error in adding controls to the map');
    }

    try {
      // Init assets into mapbox
      await initializeMapIcons(currentMap);
    } catch (_e) {
      throw new Error('Error adding images assets to map');
    }

    if (map.current !== currentMap) return;
    setImagesInitialized(true);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: specific useEffect condition
  useEffect(() => {
    // guard map is loaded
    if (!map.current || !isMapLoaded) return;
    // avoid double initialize of the map
    if (map.current._mapId === mapIdRendered.current) return;

    const currentMap = map.current;
    mapIdRendered.current = map.current._mapId;
    setImagesInitialized(false);
    initializeAll(currentMap);
    requestAnimationFrame(() => {
      if (typeof currentMap.resize === 'function') currentMap.resize();
    });
  }, [isMapLoaded]); // Only depend on isMapLoaded

  // Height only: a tall location card grows the shell. Width changes (sidebar)
  // must not resize — that blinks the WebGL canvas.
  useEffect(() => {
    const el = mapContainer.current;
    const currentMap = map.current;
    if (!el || !currentMap || !isMapLoaded) return;
    let lastHeight = el.clientHeight;
    const observer = new ResizeObserver(() => {
      const nextHeight = el.clientHeight;
      if (nextHeight === lastHeight) return;
      lastHeight = nextHeight;
      if (typeof currentMap.resize === 'function') currentMap.resize();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMapLoaded]);

  // Absolute location/deal cards do not stretch this chrome. Grow it to the
  // tallest child so the map paints behind the overflow. Skip centered dialogs:
  // their top is a percentage of this box, so counting them grows it forever.
  useEffect(() => {
    const chrome = containerRef.current;
    if (!chrome) return;
    let frame = 0;
    const sync = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        let bottom = 0;
        for (const child of chrome.children) {
          if (!(child instanceof HTMLElement) || !elementExtendsMapChrome(child)) continue;
          bottom = Math.max(bottom, child.offsetTop + child.offsetHeight);
        }
        const next = bottom > 0 ? `${bottom}px` : '';
        if (chrome.style.minHeight !== next) chrome.style.minHeight = next;
      });
    };
    const resize = new ResizeObserver(sync);
    resize.observe(chrome);
    for (const child of chrome.children) {
      if (child instanceof HTMLElement) resize.observe(child);
    }
    const mutations = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof HTMLElement) resize.observe(node);
        }
      }
      sync();
    });
    mutations.observe(chrome, { childList: true, subtree: true });
    sync();
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      mutations.disconnect();
    };
  }, []);

  // ── Zones overlay: tiles add effect ─────────────────────────────────────
  // Teardown is handled by the dedicated effect below.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to flag/load
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap || !isMapLoaded || !imagesInitialized || !zonesEnabled) return;

    const zonesLayers = new MapLayersService(currentMap);
    zonesLayers.addZonesTilesLayer(HttpService.buildTileUrl(ZONES_TILES_PATH));
    zonesLayers.orderMiddleLayers();
  }, [isMapLoaded, imagesInitialized, zonesEnabled]);

  // ── Zones overlay: owner and type filters ─────────────────────────────────
  // After the add effect, so the layers exist; separate so a change never refetches the zones.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to the selection
  useEffect(() => {
    if (!map.current || !isMapLoaded) return;
    new MapLayersService(map.current).filterZones(
      ownerSelection,
      visibleZoneTypes,
      selectedZoneName,
    );
  }, [
    ownerSelection,
    visibleZoneTypes,
    isMapLoaded,
    imagesInitialized,
    zonesEnabled,
    selectedZoneName,
  ]);

  // ── Traffic tiles: add + teardown ─────────────────────────────────────────
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to flag/load only
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap || !isMapLoaded || !imagesInitialized || !mapTilesEnabled) return;

    const trafficTilesLayers = new MapLayersService(currentMap);
    trafficTilesLayers.addTrafficTilesLayer(HttpService.buildTileUrl(TRAFFIC_TILES_PATH));
    trafficTilesLayers.orderMiddleLayers();

    return () => {
      // The map may already be torn down on unmount.
      if (map.current) {
        new MapLayersService(map.current).removeTrafficTilesLayer();
      }
    };
  }, [mapTilesEnabled, isMapLoaded, imagesInitialized, mapId]);

  // ── Basemap points of interest: site-type chips ───────────────────────────
  // The basemap import can arrive after `load`, and a satellite swap replaces
  // it, so the filter is applied again on idle until it lands and after every
  // import load. Applying twice paints the same thing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to the chips
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap || !isMapLoaded) return;
    const apply = () => {
      const kept = keepPointsOfInterest(currentMap, visiblePlaces);
      const held = holdPlacesTheTileDrops(currentMap, visiblePlaces);
      if (!kept || !held) currentMap.once('idle', apply);
    };
    apply();
    currentMap.on('style.import.load', apply);
    return () => {
      currentMap.off('idle', apply);
      currentMap.off('style.import.load', apply);
    };
  }, [visiblePlaces, isMapLoaded, mapId]);

  // ── Traffic tiles: AADT range filters ─────────────────────────────────────
  // Runs after the add effect, so the layer exists. Separate from the effect
  // above so toggling a range never tears the source down and refetches tiles.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to the filters
  useEffect(() => {
    if (!map.current || !isMapLoaded || !imagesInitialized || !mapTilesEnabled) return;
    if (!visibleTrafficRanges) return;

    applyTrafficTilesVisibility(new MapLayersService(map.current), visibleTrafficRanges);
  }, [visibleTrafficRanges, mapTilesEnabled, isMapLoaded, imagesInitialized, mapId]);

  // ── Competitor-station tiles: add + teardown ──────────────────────────────
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to flag/load only
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap || !isMapLoaded || !imagesInitialized || !mapTilesEnabled) return;

    const competitorsTilesLayers = new MapLayersService(currentMap);
    competitorsTilesLayers.addCompetitorsTilesLayer(
      HttpService.buildTileUrl(COMPETITORS_TILES_PATH),
    );
    competitorsTilesLayers.orderMiddleLayers();

    const syncFade = () => competitorsTilesLayers.syncCompetitorsTilesFade();
    syncFade();
    currentMap.on('zoom', syncFade);

    return () => {
      currentMap.off('zoom', syncFade);
      // The map may already be torn down on unmount.
      if (map.current) {
        new MapLayersService(map.current).removeCompetitorsTilesLayer();
      }
    };
  }, [mapTilesEnabled, isMapLoaded, imagesInitialized, mapId]);

  // ── Competitor-station tiles: charge-range filters ────────────────────────
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to the filters
  useEffect(() => {
    if (!map.current || !isMapLoaded || !imagesInitialized || !mapTilesEnabled) return;
    if (!visibleStationFilters) return;

    applyCompetitorsTilesVisibility(
      new MapLayersService(map.current),
      visibleStationFilters,
      clustersVisible,
    );
  }, [
    visibleStationFilters,
    clustersVisible,
    mapTilesEnabled,
    isMapLoaded,
    imagesInitialized,
    mapId,
  ]);

  // ── Mirrored HubSpot deals: add + teardown ────────────────────────────────
  // Independent of any location or zone selection — no filter effect.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to flag/load only
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap || !isMapLoaded || !imagesInitialized || !mapTilesEnabled) return;

    const dealsLayers = new MapLayersService(currentMap);
    dealsLayers.addDealsLayer();
    dealsLayers.orderMiddleLayers();

    return () => {
      // The map may already be torn down on unmount.
      if (map.current) {
        new MapLayersService(map.current).removeDealsLayer();
      }
    };
  }, [mapTilesEnabled, isMapLoaded, imagesInitialized, mapId]);

  // ── Mirrored HubSpot deals: the data behind the source ────────────────────
  // One payload for the whole mirror, re-indexed only when the mirror moved: `synced_at` is the
  // mirror's own clock, so a poll that changed nothing costs no re-index and no repaint.
  const dealsEnabled = Boolean(mapTilesEnabled && isMapLoaded && imagesInitialized);
  const { data: deals } = useDealsQuery(dealsEnabled);
  const appliedDealsKey = useRef<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap || !dealsEnabled || !deals) return;
    if (appliedDealsKey.current === deals.synced_at) return;

    appliedDealsKey.current = deals.synced_at;
    new MapLayersService(currentMap).setDealsData(deals.deals);
  }, [deals, dealsEnabled, mapId]);

  // ── Deals: owner filter ──────────────────────────────────────────────────
  // Its own effect: the data effect above is guarded by `appliedDealsKey` and would ignore this.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to the selection
  useEffect(() => {
    if (!map.current || !dealsEnabled) return;
    new MapLayersService(map.current).filterDealOwners(ownerSelection);
  }, [ownerSelection, dealsEnabled, mapId]);

  // ── Traffic tiles: hover-to-read AADT ─────────────────────────────────────
  // After the add effect, so the layer exists when the layer-scoped listeners
  // bind (React runs effects in declaration order).
  useTrafficTilesHover(map, mapTilesEnabled && isMapLoaded && imagesInitialized, mapId);

  // After the competitor layer effect, so the hit circles exist when the
  // layer-scoped listeners bind.
  useCompetitorHeatmapHover(map, mapTilesEnabled && isMapLoaded && imagesInitialized, mapId);

  // ── Competitor-station tiles: click-to-read popup ─────────────────────────
  // After the add effect, so the layer exists when the listener binds.
  const {
    setupStationsInteractions,
    selectedStation,
    popupContainer: stationPopupContainer,
  } = useStationsInteractions(map, {
    layerIds: COMPETITOR_TIER_LAYER_IDS,
    suppressRef: suppressSelectionRef,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to flag/load only
  useEffect(() => {
    if (!map.current || !isMapLoaded || !imagesInitialized || !mapTilesEnabled) return;

    return setupStationsInteractions();
  }, [mapTilesEnabled, isMapLoaded, imagesInitialized, mapId]);

  const { setupPointOfInterestInteractions, selectedPlace, clearSelectedPlace, openPlace } =
    usePointOfInterestInteractions(map, suppressSelectionRef, onPlaceOpen);
  openPlaceRef.current = openPlace;

  // A deal, a location, or a zone takes the same corner. Close the place so the two cards never stack.
  useEffect(() => {
    if (!cornerCardOpen) return;
    clearSelectedPlace();
  }, [cornerCardOpen, clearSelectedPlace]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to load only
  useEffect(() => {
    if (!map.current || !isMapLoaded) return;

    return setupPointOfInterestInteractions();
  }, [isMapLoaded, mapId]);

  // The basemap icon fades when zoomed out. This pin stays on the coordinate.
  useSelectedPlacePin(map, isMapLoaded ? selectedPlace : null);

  // ── Mirrored HubSpot deals: click-to-inspect corner card ──────────────────
  // After the add effect, so the layer exists when the listener binds.
  const noopDealSelect = useCallback(() => {}, []);
  const { setupDealsInteractions, clearSelectedDeal } = useDealsInteractions(
    map,
    onDealSelect ?? noopDealSelect,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to flag/load only
  useEffect(() => {
    if (!map.current || !isMapLoaded || !imagesInitialized || !mapTilesEnabled) return;

    return setupDealsInteractions();
  }, [mapTilesEnabled, isMapLoaded, imagesInitialized, mapId]);

  // ── Selected deal: the URL drives the unfolded pin ────────────────────────
  // Follows `?deal=<id>` so a restored or deep-linked selection unfolds its pin too. While
  // selected, the pin is held out of the clusters and past the stage filters: the card the
  // user is reading never loses its mark.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to the selection only
  useEffect(() => {
    if (!map.current || !isMapLoaded || !imagesInitialized || !mapTilesEnabled || !selectedDealId) {
      return;
    }

    new MapLayersService(map.current).setSelectedDeal(selectedDealId);
    return () => {
      if (map.current) new MapLayersService(map.current).setSelectedDeal(undefined);
    };
  }, [selectedDealId, mapTilesEnabled, isMapLoaded, imagesInitialized, mapId]);

  // ── Zones overlay: visibility-driven layer toggle ─────────────────────────
  // Propagates the on-map toggle to the Mapbox layers so they hide/show without
  // a full re-add cycle. The zoom gate is not its business any more: the source
  // carries `minzoom: ZONE_CLUSTERS_MIN_ZOOM` and each layer its own zoom range,
  // so below them Mapbox neither requests a tile nor paints one. When the flag is
  // off, zonesOverlayEnabled gates the whole block; no-op when layers don't exist
  // yet. The cluster layers are listed too — the toggle owns the overlay, not one
  // of its forms.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to the toggle
  useEffect(() => {
    if (!map.current || !isMapLoaded || !zonesOverlayEnabled) return;
    new MapLayersService(map.current).toggleLayerVisibility(
      [
        LayerId.ZONES_FILL,
        LayerId.ZONES_OUTLINE,
        LayerId.ZONES_POINT,
        LayerId.ZONE_CLUSTER,
        LayerId.ZONE_CLUSTER_COUNT,
      ],
      overlayVisible,
    );
  }, [overlayVisible, isMapLoaded, zonesOverlayEnabled]);

  // ── Zones overlay: teardown effect ──────────────────────────────────────
  // Removes the overlay when the flag goes off, when the map identity changes
  // (so the next consumer does not inherit the previous zones), and on unmount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; teardown on flag-off/map-switch/unmount
  useEffect(() => {
    return () => {
      // Guard: map may already be torn down on unmount.
      if (map.current) {
        new MapLayersService(map.current).removeZonesLayer();
      }
    };
  }, [zonesEnabled, mapId]);

  // ── Zones interactions: setup + teardown ─────────────────────────────────
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; setup/teardown on zonesEnabled/mapId
  useEffect(() => {
    if (!zonesEnabled) {
      clearSelection();
      return;
    }
    const cleanup = setupZoneInteractions();
    return () => {
      cleanup();
      clearSelection();
    };
  }, [zonesEnabled, mapId]);

  // ── Escape key dismiss ───────────────────────────────────────────────────
  // biome-ignore lint/correctness/useExhaustiveDependencies: clearSelection is stable (useState setter wrapper)
  useEffect(() => {
    if (!selectedZone) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        clearSelection();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedZone]);

  // ── Zone-selected side-effect callback ───────────────────────────────────
  // Notifies consumers (e.g. the map) after render when a zone is
  // selected, so they can run side effects (navigate) without violating the
  // React rule against side effects during render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once per selection change, keyed on selectedZone alone. onZoneSelected is intentionally excluded: consumers (e.g. MapSurface) pass a non-memoised handler recreated each render, so depending on it would re-run on every parent render and duplicate the navigation. selectedZone identity changes per click, so the latest closure is always the right one.
  useEffect(() => {
    if (selectedZone != null) {
      onZoneSelected?.(selectedZone);
    }
  }, [selectedZone]);

  function createLocationAtPlace() {
    const currentMap = map.current;
    if (!selectedPlace || !currentMap || !onCreateLocation) return;
    const place = selectedPlace;
    currentMap.easeTo({
      center: [place.longitude, place.latitude],
      zoom: Math.max(currentMap.getZoom(), PLACE_DROP_ZOOM),
    });
    clearSelectedPlace();
    onCreateLocation(place);
  }

  const renderState: MapShellRenderState = {
    map,
    isMapLoaded,
    imagesInitialized,
    containerRef,
    deals,
    placeOpen: selectedPlace != null,
  };

  const zoneSelectionNode =
    zonesEnabled && selectedZone != null && renderZoneSelection != null
      ? renderZoneSelection({ selectedZone, clearSelection, popupContainer })
      : null;

  const overlaySidebar = sidebarCtxt.state === 'expanded' && shiftChrome;

  return (
    <div
      ref={shellRef}
      className={cn(
        'relative mt-14 min-w-0 flex-1 overflow-x-hidden overflow-y-auto',
        MAP_VIEWPORT_MIN_HEIGHT_CLASS,
      )}
    >
      <div
        ref={mapContainer}
        data-slot="map-canvas"
        className="absolute inset-0"
        style={
          {
            '--map-ctrl-inset': overlaySidebar ? 'var(--sidebar-width)' : '0px',
          } as CSSProperties
        }
      />
      <div
        ref={containerRef}
        className={cn(
          '@container pointer-events-none relative z-10 transition-[margin] duration-200 ease-linear [&>*]:pointer-events-auto',
          MAP_VIEWPORT_MIN_HEIGHT_CLASS,
        )}
        style={{ marginLeft: overlaySidebar ? 'var(--sidebar-width)' : 0 }}
      >
        {children?.(renderState)}
        {zoneSelectionNode}
        {selectedStation != null &&
          stationPopupContainer != null &&
          createPortal(<StationMapPopup station={selectedStation} />, stationPopupContainer)}
        {selectedPlace != null && (
          <PointOfInterestPopup
            place={selectedPlace}
            onClose={clearSelectedPlace}
            onCreateLocation={onCreateLocation ? createLocationAtPlace : undefined}
            dialogContainer={containerRef.current ?? undefined}
          />
        )}
        {selectedDealId != null && map.current != null && renderDealSelection != null
          ? renderDealSelection({
              dealId: selectedDealId,
              clearSelection: clearSelectedDeal,
              map: map.current,
              isMapLoaded,
              imagesInitialized,
              dialogContainer: containerRef.current ?? undefined,
            })
          : null}
      </div>
    </div>
  );
}
